<template>
  <div :class="`product ${productTheme}`">
    <!-- .wine-product-header -->
    <div class="wine-product-header">
      <b-container>
        <b-row>
          <b-col cols="12">
            <!-- / wine-product-header-inner -->
            <div class="wine-product-header-inner">
              <!-- Product image display -->
              <div class="product-image-display">
                <ProductImageDisplay :images="item.imagesGallery" />
              </div>
              <!-- / Product image display -->

              <!-- Product Info -->
              <div class="product-info">
                <!-- Product Intro -->
                <div class="product-intro">
                  <div
                    v-if="showSeries"
                    class="product-series"
                  >
                    {{ item.seriesLabel }}
                  </div>
                  <div
                    v-if="item.brand && item.brand !== 'unknown'"
                    class="product-brand"
                  >
                    {{ item.brand }}
                  </div>
                  <div class="product-title">
                    <h1>{{ item.title }}</h1>
                    <div
                      v-if="item.region && item.region !== 'unknown'"
                      class="product-region"
                    >
                      {{ item.region }}
                    </div>
                  </div>

                  <template v-if="accoladeCount > 0">
                    <a
                      href="#wine-features-region"
                      @click="triggerAwardsTab"
                    >
                      <div class="accolades-highlight">
                        <div class="icon">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 50 77.386"
                          >
                            <path
                              class="cls-1"
                              d="M40.141,32.494h0c-.377-.288-.763-.565-1.154-.831a.618.618,0,0,1-.09-.061A24.649,24.649,0,0,0,30.518,28,25.211,25.211,0,0,0,19.48,28a24.673,24.673,0,0,0-8.448,3.654l0,0c-.4.266-.785.547-1.169.838a25,25,0,1,0,30.282,0ZM25,74.755a22.371,22.371,0,1,1,9.423-42.662.267.267,0,0,1,.046.02l.015.007.012.005.078.041h0l.213.108c.14.066.279.132.416.2.095.049.188.1.281.149.124.066.247.129.369.2.105.056.207.117.31.178s.223.125.33.191.22.134.33.2.2.122.3.188.225.149.335.225c.095.061.19.125.283.188.11.076.218.156.325.234s.189.132.281.2.184.142.277.213.212.161.315.244c.022.017.041.034.061.049.174.139.342.278.511.423l0,0A22.371,22.371,0,0,1,25,74.755Z"
                            />
                            <path
                              class="cls-1"
                              d="M31.11,25.381a27.2,27.2,0,0,1,7.877,3.107L49.42,0H31.779L26.431,14.609l3.882,10.6C30.579,25.264,30.846,25.32,31.11,25.381Z"
                            />
                            <path
                              class="cls-1"
                              d="M18.888,25.376A28.218,28.218,0,0,1,27.3,24.8L18.216,0H.573L11.011,28.488A27.41,27.41,0,0,1,18.888,25.376Z"
                            />
                            <path
                              class="cls-1"
                              d="M37.768,37.405a19.546,19.546,0,0,0-4.424-2.846c-.032-.014-.064-.031-.1-.048a19.69,19.69,0,1,0,4.52,2.894Zm-8.1,18.363.953,5.569-5-2.631a1.351,1.351,0,0,0-1.252,0l-5,2.631.956-5.569a1.355,1.355,0,0,0-.386-1.19l-4.048-3.945,5.6-.812a1.348,1.348,0,0,0,1.012-.735l2.5-5.07,2.5,5.07a1.346,1.346,0,0,0,1.012.735l5.592.812-4.047,3.945A1.343,1.343,0,0,0,29.67,55.768Z"
                            />
                          </svg>
                        </div>
                        <div class="label">{{ accoladeCount }} Awards & Reviews</div>
                      </div>
                    </a>
                  </template>

                  <!-- .product-description -->
                  <div class="product-description">
                    <read-more
                      v-if="item.abstract"
                      :max-chars="240"
                      :text="item.abstract"
                      class="item-description"
                    />
                  </div>
                  <!-- / .product-description -->
                </div>
                <!-- / Product Intro -->

                <!-- Key Info Display -->
                <KeyInfoDisplay :content="item" />
                <!-- / Key Info Display -->

                <div
                  v-if="item.product_links && item.product_links.length > 0"
                  class="product-links-element"
                >
                  <label for="links">{{ linksTitle }}</label>
                  <b-form-select
                    id="links"
                    v-model="currentLink"
                    :options="linkOptions"
                    @change="linkChange"
                  />
                </div>

                <cart-add-block
                  :key="$route.path"
                  :node="item"
                  :overriderrp="item.rrp"
                />

                <TastingNotes :notes="item.prev_tasting_notes" />
                <!-- / Tasting Notes -->
              </div>
              <!-- / .product-info-header-inner -->
            </div>
            <!-- / .wine-product-header-inner -->
          </b-col>
        </b-row>
      </b-container>
    </div>
    <!-- / .wine-product-header -->

    <deferred-content-placeholder v-if="!productDetailIsComplete" />
    <wine-feature-section
      v-else
      ref="featureSection"
      :wine-making-notes-data="item.wine_making"
      :reviews-data="item.reviews"
      :tasting-notes="item.tasting_notes"
      :awards-data="item.awards"
      :technical-notes-data="item.technical_notes"
      :faqs-data="faqs"
      :initial-pane="initialFeatureSectionPane"
    />

    <relatedProducts
      v-if="relatedProducts.length > 0"
      :related-products="relatedProducts"
      :theme="productTheme"
      :hover-card-setting="hoverCardSetting"
    />
  </div>
</template>

<script>
// Components
import ReadMore from './ReadMore.vue'
import ProductImageDisplay from './ProductImageDisplay'
import TastingNotes from './TastingNotes.vue'
import KeyInfoDisplay from './KeyInfoDisplay'
import wineFeatureSection from './components/feature-section'
import CartAddBlock from '@aspedia/vuejs-ecommerce-ui/components/cart-add-block/0002/cart-add-block'
import relatedProducts from '../common/related-products'
import DeferredContentPlaceholder from 'components/generic/loaders/deferred-content-placeholder'

// Mixin
import filters from '../common/mixins/filters'
import methods from '../common/mixins/methods'
import preparation from '../common/mixins/preparation'
import settings from '../common/mixins/settings'
import productDetailLazyLoad from 'src/app/product-detail-lazy-load-mixin'

export default {
  name: 'wine-page',
  components: {
    TastingNotes,
    relatedProducts,
    ReadMore,
    'deferred-content-placeholder': DeferredContentPlaceholder,
    ProductImageDisplay,
    KeyInfoDisplay,
    wineFeatureSection,
    CartAddBlock
  },
  mixins: [filters, methods, preparation, settings, productDetailLazyLoad],
  computed: {
    linksTitle() { return this.item.product_links_name ? this.item.product_links_name : 'Options' },
    currentLink: {
      get() {
        if (this.linkOptions.find(({ value }) => value === this.$route.path)) return this.$route.path
        if (this.linkOptions.find(({ value }) => value === this.$route.path.slice(1))) return this.$route.path.slice(1)
        return ''
      },
      set() {}
    },
    linkOptions() {
      const options = []
      for (const { link } of this.item.product_links) {
        if (link.url && link.title) {
          options.push({ value: link.url, text: link.title })
        }
      }
      return options
    },
    showSeries() { return this.$store.getters.getModuleSetting('theme', 'showSeriesLabel', true) },
    initialFeatureSectionPane() {
      const registrySettings = this.$store.getters['getModuleSetting']('theme', 'concilio-settings')

      if (registrySettings !== null) {
        const productDetailsPageSettingsExist = Object.keys(registrySettings).includes('productDetailPage')
        return productDetailsPageSettingsExist
          ? registrySettings.productDetailPage['initial-details-section'] ?? ''
          : ''
      }
      else {
        return ''
      }
    },
    // reviews reads a field the SSR-inlined state can defer (see
    // product-detail-lazy-load-mixin) - hide the accolades banner until
    // it's loaded rather than crash on the read.
    accoladeCount() {
      if (!this.productDetailIsComplete) return 0
      return (this.item.reviews.length + this.item.awards.length)
    }
  },
  methods: {
    linkChange(url) {
      if (url.includes('://')) {
        window.location.href = url
        return
      }
      this.$router.push(this.addSlash(url))
    },
    addSlash(url) {
      if (url.startsWith('/')) return url
      return `/${url}`
    },
    triggerAwardsTab: function() {
      return this.$refs.featureSection.changePane('reviews-and-awards')
    }
  }
}
</script>

<style lang="scss" scoped>
@import "~@scss/custom.scss";

::v-deep  .cool-lightbox .cool-lightbox__slide img {
  background: white;
}

.accolades-highlight {
  display: flex;
  align-items: center;
  margin-bottom: 1em;

  .label {
    color: var(--vc-link-color);
    font-size: 1.15em;
  }

  .icon {
    width: 1.35em;
    margin-right: 1em;
    fill: var(--vc-link-color);
  }
}

.wine-product {
  //background container for header
  &-header {
    background-color: var(--vc-background-color);
    color: var(--vc-text-color);
    padding: 3em 0em;
  }

  //responsive structure for header image and product info
  &-header-inner {
    display: flex;
    justify-content: space-between;
    flex-direction: column;

    @include media-breakpoint-up(lg) {
      flex-direction: row;
      align-items: flex-start;
    }

    .product {
      &-info {
        @include media-breakpoint-up(lg) { width: 55%; }
        @include media-breakpoint-up(xl) { width: 45%; }
      }

      &-image-display {
        width: 100%;

        @include media-breakpoint-up(lg) { width: calc(45% - 2em); }
        @include media-breakpoint-up(xl) { width: calc(55% - 5em); }
      }
    }
  }
}

//product introduction component
.product {
  &-intro {
    display: flex;
    flex-direction: column;
    margin-bottom: 2em;
  }

  &-title {
    margin-bottom: 1em;
  }
}

.item-description {
 ::v-deep p {
    &:last-of-type {
      display: inline;
      margin-bottom: 0;
    }
  }
}

.reviews-awards {
  padding: 3em 0em;
}

.login-cta {
  color: var(--cart-primary-color);
  align-items: center;
  display: flex;
  justify-content: center;

  .icon {
    fill: var(--cart-primary-color);
    width: 2em;

    &-container {
      width: 2em;
      height: 2em;
      display: flex;
      margin-right: 1em;
    }
  }
}

//cart region
.cart-block {
  --cart-primary-color: var(--vc-link-color);
  --cart-border-color: var(--vc-border-color);

  --cart-border-radius: 3px;
  border: 1px solid;
  border-color: var(--cart-border-color);
  border-radius: var(--cart-border-radius);
  padding: 1em;
  margin-bottom: 2em;

  @include media-breakpoint-up(lg) { padding: 2em; }

  .cart-element { margin-bottom: 1rem; }

  .form-control,
  .custom-select { border-color: var(--cart-border-color); }
}

.price {
  &-display {
    display: flex;
    flex-direction: row;
    border-bottom: 1px solid;
    border-color: var(--cart-border-color);

    .price {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      padding: 1em 1.5em;
      align-items: center;
      line-height: initial;
      width: 100%;

      &:not(:last-child) {
        border-right: 1px solid;
        border-color: var(--cart-border-color);
      }

      .price-label {
        font-size: 0.8em;
        margin-bottom: 0.5rem;
      }
    }
  }

  &-amount {
    font-size: 1.4em;
    @include media-breakpoint-up(lg) {
      font-size: 1.8em;
    }
  }
}

.cart-button {
  background: var(--cart-primary-color);
  color: white;
  padding: 0.75em 1.5em;
  width: 100%;
  border-radius: 3px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  border: 0px;
}

.elements {
  display: flex;
  flex-direction: row;
  justify-content: space-between; //we space this edge to edge
}

.element {
  &:only-child { width: 100%; }
  &:not(:last-child),
  &:not(:first-child) { width: calc(50% - 0.75em); }
}

.page-content {
  background-color: var(--vc-background-color);
  color: var(--vc-text-color);

  a { color: var(--vc-link-color); }
}

.input-form {
  border-radius: 6px;
  border: 2px solid var(--vc-input-border-color);
  background-color: var(--vc-background-color);
  color: var(--vc-input-text-color);
  width: 100%;
  padding: 0.5em 1em;
  outline: none;
  display: flex;
  flex-direction: column;

  @include transition(border-color 0.3s ease-in-out);
  @include hover-focus { border: 2px solid var(--vc-input-border-hover-color); }

  &.active {
    border: 2px solid var(--vc-input-border-hover-color);
    color: var(--vc-input-text-color);

    @include hover-focus { border: 2px solid var(--vc-accent-contrast-color); }
  }

  &::-webkit-input-placeholder,
  &::placeholder { color: var(--vc-input-placeholder-color); }
}

// Tabs
.menu-tabs {
  padding-bottom: 3em;

 ::v-deep .nav-pills {
    display: flex;
    list-style: none;
    outline: none;
    padding: 0em;
    justify-content: center;
    flex-direction: row;
    flex-wrap: nowrap;
    margin-left: auto;
    margin-right: auto;
    padding: 2em 0em;

    .nav-item {
      align-items: center;
      display: flex;
      padding: 1em 0.25em;
      font-size: 1em;

      @include media-breakpoint-up(md) {
        padding: 0.5em 1em;
        display: flex;
        width: 100%;
        max-width: 33%;
        justify-content: center;

        &:not(:last-child) {
          border-right: 2px solid;
          border-color: var(--main-border-color);
        }
      }

      @include media-breakpoint-up(lg) { max-width: 20%; }

      .nav-link {
        outline: none;
        text-decoration: none;
        background-color: transparent;
        border-radius: 0px;
        color: var(--vc-link-color);
        text-align: center;
        padding: 0.5em 0em;
        transition: transform 0.3s ease-in-out;

        &:hover { box-shadow: 0px 2px 0px var(--main-text); }

        &.active {
          color: var(--vc-link-color);
          box-shadow: 0px 2px 0px var(--vc-link-color);
        }
      }

      &:hover {
        .nav-link {
          transform: translateY(-0.5em);
        }
      }
    }
  }

 ::v-deep .menu-set-header {
    margin-bottom: 2em;
  }

  .menu-set-category-header {
    margin-bottom: 2em;
  }
}

.product::v-deep .a-carousel .swiper-arrow .icon-container svg path {
  fill: var(--vc-link-color);
}

.product::v-deep .a-carousel .swiper-arrow.swiper-button-disabled {
  opacity: 0;
}
</style>
